import {
  formatCatalogTrackDisplayTitle,
  formatLanguageDisplay,
  formatTrackTitle,
  getTrackLanguageKey
} from './roadmapDisplay';

function normalizeSearchToken(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

function sanitizeLearnMoreQuery(text) {
  return String(text || '')
    .trim()
    .replace(/\s*&\s*/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function searchTextIncludesLanguage(text, languageLabel) {
  const language = normalizeSearchToken(languageLabel);
  if (!language || !text) return false;

  const normalized = normalizeSearchToken(text);
  if (normalized === language) return true;
  if (normalized.startsWith(`${language} `)) return true;

  const pattern = new RegExp(`\\b${language.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
  return pattern.test(normalized);
}

function languageIsImpliedByTrackOrTopic(text, languageLabel) {
  if (searchTextIncludesLanguage(text, languageLabel)) return true;

  const normalized = normalizeSearchToken(text);
  const language = normalizeSearchToken(languageLabel);
  return language === 'swift' && /\bswiftui?\b/i.test(normalized);
}

/**
 * Prefer human-readable catalog titles for search (keeps "iOS SwiftUI" intact).
 *
 * @param {{ title?: string, language?: unknown }|null|undefined} track
 * @param {string} languageKey
 * @returns {string}
 */
function formatSearchTrackLabel(track, languageKey) {
  const rawTitle = track?.title;
  if (typeof rawTitle === 'string' && rawTitle.trim()) {
    const cleaned = rawTitle.replace(/[_-]+/g, ' ').trim();
    if (cleaned) return cleaned;
  }

  return (
    formatCatalogTrackDisplayTitle(languageKey, rawTitle, track) ||
    formatTrackTitle(rawTitle)
  );
}

/**
 * Build an external search query for broad engines (YouTube, Google).
 *
 * @param {{ title?: string, language?: unknown }|null|undefined} track
 * @param {string|null|undefined} topicTitle
 * @returns {string}
 */
export function buildCookbookLearnMoreSearchQuery(track, topicTitle) {
  const languageKey = getTrackLanguageKey(track);
  const languageLabel = formatLanguageDisplay(languageKey);
  const trackLabel = sanitizeLearnMoreQuery(formatSearchTrackLabel(track, languageKey));
  const topic = sanitizeLearnMoreQuery(topicTitle);
  const combinedWithoutLanguage = [trackLabel, topic].filter(Boolean).join(' ').trim();

  const parts = [];
  if (
    languageLabel &&
    !searchTextIncludesLanguage(combinedWithoutLanguage, languageLabel)
  ) {
    parts.push(languageLabel);
  }
  if (trackLabel) parts.push(trackLabel);
  if (topic) parts.push(topic);

  return parts.join(' ').trim() || languageLabel || topic;
}

/**
 * Shorter query for tutorial sites — full track+language+topic strings are too noisy.
 *
 * @param {{ title?: string, language?: unknown }|null|undefined} track
 * @param {string|null|undefined} topicTitle
 * @returns {string}
 */
export function buildCookbookFocusedSearchQuery(track, topicTitle) {
  const languageKey = getTrackLanguageKey(track);
  const languageLabel = formatLanguageDisplay(languageKey);
  const trackLabel = sanitizeLearnMoreQuery(formatSearchTrackLabel(track, languageKey));
  const topic = sanitizeLearnMoreQuery(topicTitle);

  let query = [trackLabel, topic].filter(Boolean).join(' ').trim();
  if (!query) return languageLabel || '';

  if (languageLabel && !languageIsImpliedByTrackOrTopic(query, languageLabel)) {
    query = `${languageLabel} ${query}`.trim();
  }

  return query;
}

/** @param {string} query */
export function buildCookbookTutorialSearchQuery(query) {
  const base = sanitizeLearnMoreQuery(query);
  if (!base) return 'programming tutorial';
  if (/\btutorial\b/i.test(base)) return base;
  return `${base} tutorial`.trim();
}

export function buildYouTubeSearchUrl(query) {
  return `https://www.youtube.com/results?search_query=${encodeURIComponent(query)}`;
}

export function buildYouTubeTutorialSearchUrl(track, topicTitle) {
  const query = buildCookbookTutorialSearchQuery(buildCookbookLearnMoreSearchQuery(track, topicTitle));
  return buildYouTubeSearchUrl(query);
}

export function buildGoogleSearchUrl(query) {
  return `https://www.google.com/search?q=${encodeURIComponent(query)}`;
}

export function buildGoogleTutorialSearchUrl(track, topicTitle) {
  const query = buildCookbookTutorialSearchQuery(buildCookbookLearnMoreSearchQuery(track, topicTitle));
  return buildGoogleSearchUrl(query);
}

/**
 * Tutorial-first sites. These publish lessons and walkthroughs — not API reference.
 *
 * @type {Record<string, Array<{ id: string, label: string, icon: string, buildUrl: (query: string) => string }>>}
 */
export const TUTORIAL_SITES_BY_LANGUAGE = {
  swift: [
    {
      id: 'hacking-with-swift',
      label: 'Hacking with Swift',
      icon: 'hacking-with-swift',
      buildUrl: (query) =>
        `https://www.hackingwithswift.com/search?q=${encodeURIComponent(query)}`
    }
  ],
  python: [
    {
      id: 'real-python',
      label: 'Real Python',
      icon: 'real-python',
      buildUrl: (query) => `https://realpython.com/search?q=${encodeURIComponent(query)}`
    }
  ],
  javascript: [
    {
      id: 'javascript-info',
      label: 'javascript.info',
      icon: 'javascript-info',
      buildUrl: (query) =>
        `https://javascript.info/search?query=${encodeURIComponent(query)}`
    }
  ],
  typescript: [
    {
      id: 'typescript-handbook',
      label: 'TypeScript Handbook',
      icon: 'typescript-handbook',
      buildUrl: (query) =>
        `https://www.typescriptlang.org/docs/search?q=${encodeURIComponent(query)}`
    }
  ],
  java: [
    {
      id: 'baeldung',
      label: 'Baeldung',
      icon: 'baeldung',
      buildUrl: (query) => `https://www.baeldung.com/?s=${encodeURIComponent(query)}`
    }
  ],
  csharp: [
    {
      id: 'microsoft-learn',
      label: 'Microsoft Learn',
      icon: 'microsoft-learn',
      buildUrl: (query) =>
        `https://learn.microsoft.com/en-us/search/?terms=${encodeURIComponent(`${query} tutorial`)}`
    }
  ],
  go: [
    {
      id: 'go-tour',
      label: 'Go Docs',
      icon: 'go-tour',
      buildUrl: (query) => `https://go.dev/search?q=${encodeURIComponent(`${query} tutorial`)}`
    }
  ],
  rust: [
    {
      id: 'rust-book',
      label: 'The Rust Book',
      icon: 'rust-book',
      buildUrl: (query) =>
        `https://doc.rust-lang.org/?search=${encodeURIComponent(query)}`
    }
  ],
  kotlin: [
    {
      id: 'kotlin-docs',
      label: 'Kotlin Docs',
      icon: 'kotlin-docs',
      buildUrl: (query) =>
        `https://kotlinlang.org/docs/search.html?q=${encodeURIComponent(query)}`
    }
  ],
  php: [
    {
      id: 'w3schools',
      label: 'W3Schools',
      icon: 'w3schools',
      buildUrl: (query) =>
        `https://www.w3schools.com/tags/ref_search.asp?q=${encodeURIComponent(query)}`
    }
  ],
  ruby: [
    {
      id: 'ruby-docs',
      label: 'Ruby Docs',
      icon: 'ruby-docs',
      buildUrl: (query) => `https://ruby-doc.org/search.html?q=${encodeURIComponent(query)}`
    }
  ],
  cpp: [
    {
      id: 'learncpp',
      label: 'Learn CPP',
      icon: 'learncpp',
      buildUrl: (query) =>
        `https://www.learncpp.com/?s=${encodeURIComponent(query)}`
    }
  ]
};

/** Tutorial sites that work well across most programming languages. */
export const UNIVERSAL_TUTORIAL_SITES = [
  {
    id: 'geeksforgeeks',
    label: 'GeeksforGeeks',
    icon: 'geeksforgeeks',
    buildUrl: (query) =>
      `https://www.geeksforgeeks.org/search/?query=${encodeURIComponent(query)}`
  },
  {
    id: 'freecodecamp',
    label: 'freeCodeCamp',
    icon: 'freecodecamp',
    buildUrl: (query) =>
      `https://www.freecodecamp.org/news/search/?query=${encodeURIComponent(`${query} tutorial`)}`
  },
  {
    id: 'devto',
    label: 'DEV Community',
    icon: 'devto',
    buildUrl: (query) => `https://dev.to/search?q=${encodeURIComponent(`${query} tutorial`)}`
  }
];

/**
 * Build learn-more menu options that prioritize tutorials over reference/Q&A.
 *
 * @param {{ title?: string, language?: unknown }|null|undefined} track
 * @param {string|null|undefined} topicTitle
 * @returns {Array<{ id: string, label: string, url: string, icon: string }>}
 */
export function getCookbookLearnMoreOptions(track, topicTitle) {
  const focusedQuery = buildCookbookFocusedSearchQuery(track, topicTitle);
  if (!focusedQuery) return [];

  const languageKey = getTrackLanguageKey(track);
  const options = [
    {
      id: 'youtube',
      label: 'Video tutorials on YouTube',
      url: buildYouTubeTutorialSearchUrl(track, topicTitle),
      icon: 'youtube'
    },
    {
      id: 'google',
      label: 'Written tutorials on Google',
      url: buildGoogleTutorialSearchUrl(track, topicTitle),
      icon: 'google'
    }
  ];

  const seen = new Set(options.map((option) => option.id));

  for (const site of TUTORIAL_SITES_BY_LANGUAGE[languageKey] || []) {
    if (seen.has(site.id)) continue;
    seen.add(site.id);
    options.push({
      id: site.id,
      label: `Tutorials on ${site.label}`,
      url: site.buildUrl(focusedQuery),
      icon: site.icon
    });
  }

  for (const site of UNIVERSAL_TUTORIAL_SITES) {
    if (seen.has(site.id)) continue;
    seen.add(site.id);
    options.push({
      id: site.id,
      label: `Tutorials on ${site.label}`,
      url: site.buildUrl(focusedQuery),
      icon: site.icon
    });
  }

  return options;
}

export function openExternalSearch(url) {
  if (!url) return;
  window.open(url, '_blank', 'noopener,noreferrer');
}
